/**
 * Displays a single etymology root (or meaning) with full detail.
 * Format: "Meaning ID: {{id}} ({{typeName}}, {{languageName}}){{displaySpelling}}"
 * Reusable across create-entry-dialog, create-entry-page, and edit-entry-page.
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { t } from '../utils/i18n.js'
import '@material/web/button/outlined-button.js'
import '@tlenexkoyotl/xamix-element'
import styles from './etymology-root-display.css?inline'

const XAMIX_SYSTEM_ID = '1'

export interface EtymologyRootDisplayData {
  id: string
  typeName: string
  languageName: string
  displaySpelling?: string
  definition?: string
  definitionLanguageName?: string
  /** EtymologyRoot row id for delete operations (when different from display id) */
  rowId?: string
  spellings?: Array<{ text: string; systemId: string; systemName?: string }>
}

@customElement('etymology-root-display')
export class EtymologyRootDisplay extends LitElement {
  /** Root/meaning data to display */
  @property({ type: Object }) root!: EtymologyRootDisplayData

  /** Label type: 'meaning' for Meaning ID, 'word' for Word ID */
  @property({ type: String }) labelType: 'meaning' | 'word' = 'meaning'

  /** Show remove button */
  @property({ type: Boolean }) removable = false

  /** Disable remove button (e.g. while deleting) */
  @property({ type: Boolean }) removeDisabled = false

  static styles = css`${unsafeCSS(styles)}`

  private handleRemove() {
    this.dispatchEvent(new CustomEvent('remove', { bubbles: true, composed: true }))
  }

  render() {
    if (!this.root) return html``

    const displaySpelling = this.root.displaySpelling ? ` — ${this.root.displaySpelling}` : ''
    const labelKey = this.labelType === 'word' ? 'createDialog.wordFromSearchLabel' : 'createDialog.meaningOptionLabel'
    const label = t(labelKey, {
      // id: `<a href="/meaning-etymologies/${this.root.id}">this.root.id</a>`,
      id: this.root.id,
      typeName: this.root.typeName ? t(this.root.typeName) : '',
      languageName: this.root.languageName ? t(this.root.languageName) : '',
      displaySpelling,
    })

    const spellings = this.root.spellings ?? []
    const definition = this.root.definition
    const definitionLanguage = this.root.definitionLanguageName

    return html`
      <div class="etymology-root-row">
        <div class="etymology-root-label">
          <div><h3><a href="/meaning-etymologies/${this.root.id}">${label}</a></h3></div>
          ${spellings.length > 0 ? html`
            <div class="etymology-root-spellings">
              ${spellings.map(s => html`
                <div class="etymology-root-spelling">
                  ${s.systemId === XAMIX_SYSTEM_ID
                    ? html`<xamix-element dir="rtl" text-input="${s.text}" root="/node_modules/@tlenexkoyotl/xamix-element" font-size="4" bold adaptable></xamix-element>`
                    : html`<h2>${s.text}</h2>`}
                  ${s.systemName ? html`<h3 class="etymology-root-system"> · ${t(s.systemName)}</h3>` : ''}
                </div>
              `)}
            </div>
          ` : ''}
        </div>
        ${definition ? html`
          <div class="etymology-root-definition">
            <div class="definition-label">
              ${t('etymology.definitionInUiLanguage')}
              ${definitionLanguage ? html`<span class="definition-language"> · ${t(definitionLanguage)}</span>` : ''}
            </div>
            <div class="definition-text">${definition}</div>
          </div>
        ` : ''}
        ${this.removable ? html`
          <md-outlined-button type="button" class="row-remove" ?disabled=${this.removeDisabled}
            @click=${this.handleRemove}>${t('common.delete')}</md-outlined-button>
        ` : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'etymology-root-display': EtymologyRootDisplay
  }
}
